import { useEffect, useMemo, useRef, useState } from "react";
import {
  ReportHeader,
  ReportIcon,
  ReportKpiCard,
  ReportState,
} from "./ReportUi";
import { useAuth } from "../../contexts/AuthContext";

const dateRangeOptions = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "custom", label: "Custom Range" },
];

type SummaryResponse = {
  metrics: {
    unique_visitors: number;
    sessions: number;
    page_views: number;
    listing_views: number;
    agent_site_views: number;
    vcard_views: number;
    leads: number;
    whatsapp_clicks: number;
    call_clicks: number;
    email_clicks: number;
    finance_clicks: number;
  };
  charts: {
    traffic_over_time: Array<{
      date: string;
      visitors: number;
      page_views: number;
      listing_views: number;
      leads: number;
    }>;
    traffic_sources: Array<{ source: string; value: number }>;
    device_split: Array<{ device: string; value: number }>;
  };
  tables: {
    top_agents: Array<{
      agent: string;
      market_centre: string;
      unique_visitors: number;
      page_views: number;
      listing_views: number;
      agent_site_views: number;
      vcard_views: number;
      leads: number;
      whatsapp_clicks: number;
      finance_clicks: number;
      kwuid: string | null;
    }>;
    top_market_centres: Array<{
      market_centre: string;
      unique_visitors: number;
      page_views: number;
      listing_views: number;
      leads: number;
      finance_clicks: number;
      market_centre_id: string | null;
    }>;
    most_viewed_listings: Array<{
      listing_number: string;
      suburb: string;
      presenting_agent: string;
      market_centre: string;
      views: number;
      unique_visitors: number;
      leads: number;
      kwuid: string | null;
    }>;
  };
  filters: {
    market_centres: Array<{ id: string; name: string }>;
    agents: Array<{
      kwuid: string;
      name: string;
      market_centre: string | null;
    }>;
  };
  lead_count_source: string;
  date_range: { start_date: string; end_date: string };
};

// Manual Current Report period: 1st of the reporting month through end of yesterday (Africa/Johannesburg).
function computeManualPeriod(): { startLabel: string; endLabel: string; startIso: string; endIso: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Johannesburg",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? "0");
  const todayUtc = new Date(Date.UTC(get("year"), get("month") - 1, get("day")));
  const yesterday = new Date(todayUtc);
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const monthStart = new Date(Date.UTC(yesterday.getUTCFullYear(), yesterday.getUTCMonth(), 1));
  const label = (value: Date) =>
    value.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
  const iso = (value: Date) => value.toISOString().slice(0, 10);
  return { startLabel: label(monthStart), endLabel: label(yesterday), startIso: iso(monthStart), endIso: iso(yesterday) };
}

function manualConfigStatus(config: { enabled: boolean; recipients: Array<{ id: string }> } | undefined): "Configured" | "Not Configured" | "Disabled" {
  if (!config) return "Not Configured";
  if (!config.enabled) return "Disabled";
  return config.recipients.length > 0 ? "Configured" : "Not Configured";
}

function EmailReportsTab() {
  const token = localStorage.getItem("kwsa_auth_token");
  const [scope, setScope] = useState<"REGION" | "MARKET_CENTRE">("REGION");
  const [marketCentre, setMarketCentre] = useState("");
  const [marketCentres, setMarketCentres] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [preview, setPreview] = useState<{
    subject: string;
    html: string;
    recipients: string[];
  } | null>(null);
  const [message, setMessage] = useState("");
  const [associates, setAssociates] = useState<
    Array<{ id: string; name: string; email: string; job_title: string | null }>
  >([]);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [configs, setConfigs] = useState<Array<{ scope_type: string; scope_id: string; enabled: boolean; recipients: Array<{ id: string }> }>>([]);
  const [history, setHistory] = useState<Array<{ created_at: string; scope_type: string; scope_id: string; send_type: string; recipient_count: number; report_start: string; report_end: string; status: string }>>([]);
  const [historyScope, setHistoryScope] = useState('');
  const [historyType, setHistoryType] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');
  const [confirmAction, setConfirmAction] = useState<'REGION' | 'MARKET_CENTRE' | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [previewRequestId, setPreviewRequestId] = useState(0);

  // Manual Current Report state — independent of the saved Monday schedule/recipients above.
  const [manualScope, setManualScope] = useState<"REGION" | "MARKET_CENTRE">("REGION");
  const [manualMarketCentre, setManualMarketCentre] = useState("");
  const [manualAssociates, setManualAssociates] = useState<
    Array<{ id: string; name: string; email: string; job_title: string | null }>
  >([]);
  const [manualRecipients, setManualRecipients] = useState<string[]>([]);
  const [manualPreview, setManualPreview] = useState<{ subject: string; html: string; recipients: string[] } | null>(null);
  const [manualMessage, setManualMessage] = useState("");
  const [manualPreviewMode, setManualPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [manualConfirmOpen, setManualConfirmOpen] = useState(false);
  const manualPeriod = useMemo(() => computeManualPeriod(), []);

  useEffect(() => {
    fetch("/api/reports/kwhomes-email/market-centres", {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((res) => res.json())
      .then((body) => setMarketCentres(body.rows ?? []))
      .catch(() => setMarketCentres([]));
  }, [token]);

  useEffect(() => {
    const params = marketCentre
      ? `?market_centre_id=${encodeURIComponent(marketCentre)}`
      : "";
    fetch(`/api/reports/kwhomes-email/associates${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((res) => res.json())
      .then((body) => setAssociates(body.rows ?? []))
      .catch(() => setAssociates([]));
  }, [token, marketCentre]);

  const refreshAdminData = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const [configResponse, historyResponse] = await Promise.all([
      fetch('/api/reports/kwhomes-email/configs', { headers }),
      fetch('/api/reports/kwhomes-email/history', { headers }),
    ]);
    if (configResponse.ok) setConfigs((await configResponse.json()).rows ?? []);
    if (historyResponse.ok) setHistory((await historyResponse.json()).rows ?? []);
  };

  useEffect(() => { void refreshAdminData(); }, [token]);

  useEffect(() => {
    const config = configs.find((item) => item.scope_type === scope && item.scope_id === (scope === 'REGION' ? 'KWSA' : marketCentre));
    if (config) {
      setSelectedRecipients(config.recipients.map((recipient) => recipient.id));
    } else if (scope === 'MARKET_CENTRE' && marketCentre) {
      setSelectedRecipients(associates.filter((associate) => /operating partner/i.test(associate.job_title ?? '')).map((associate) => associate.id));
    } else if (scope === 'REGION') {
      setSelectedRecipients([]);
    }
  }, [configs, scope, marketCentre, associates]);

  useEffect(() => {
    setPreview(null);
    setMessage('Preview not generated.');
  }, [scope, marketCentre]);

  useEffect(() => {
    const params = manualScope === "MARKET_CENTRE" && manualMarketCentre ? `?market_centre_id=${encodeURIComponent(manualMarketCentre)}` : "";
    fetch(`/api/reports/kwhomes-email/associates${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((res) => res.json())
      .then((body) => setManualAssociates(body.rows ?? []))
      .catch(() => setManualAssociates([]));
  }, [token, manualScope, manualMarketCentre]);

  useEffect(() => {
    setManualRecipients([]);
    setManualPreview(null);
    setManualMessage("");
  }, [manualScope, manualMarketCentre]);

  const saveConfig = async () => {
    const response = await fetch("/api/reports/kwhomes-email/configs", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        scope_type: scope,
        scope_id: marketCentre,
        enabled: selectedRecipients.length > 0,
        associate_ids: selectedRecipients,
      }),
    });
    setMessage(
      response.ok
        ? "Configuration saved locally."
        : ((await response.json()).error ?? "Unable to save configuration."),
    );
    if (response.ok) void refreshAdminData();
  };

  const loadPreview = async (send = false) => {
    if (!send && scope === 'MARKET_CENTRE' && !marketCentre) {
      setPreview(null);
      setMessage('Select a Market Centre before generating a preview.');
      return;
    }
    const requestId = previewRequestId + 1;
    setPreviewRequestId(requestId);
    const payload = {
      scope,
      scope_id: marketCentre,
      scope_name: marketCentres.find((item) => item.id === marketCentre)?.name,
      send_type: send ? "TEST" : undefined,
    };
    const previewQuery = new URLSearchParams({ scope, _preview: `${Date.now()}-${requestId}` });
    if (scope === 'MARKET_CENTRE') {
      previewQuery.set('scope_id', marketCentre);
      previewQuery.set('scope_name', marketCentres.find((item) => item.id === marketCentre)?.name ?? '');
    }
    const response = await fetch(
      send
        ? "/api/reports/kwhomes-email/send-dry-run"
        : `/api/reports/kwhomes-email/preview?${previewQuery.toString()}`,
      {
        method: send ? "POST" : "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(send ? { "Content-Type": "application/json" } : {}),
        },
        body: send ? JSON.stringify(payload) : undefined,
      },
    );
    const body = await response.json();
    if (!response.ok) {
      setMessage(body.error ?? "Unable to build report.");
      return;
    }
    setPreview(body);
    setMessage(
      send
        ? "Dry run recorded. No email was delivered."
        : "Preview generated locally.",
    );
  };

  const sendRealTest = async () => {
    const scopeLabel = scope === 'REGION' ? 'KWSA Regional' : (marketCentres.find((item) => item.id === marketCentre)?.name ?? 'Selected Market Centre');
    if (!window.confirm(`Send the real ${scopeLabel} test email to ${'ronald.vanscheltema@kwsa.co.za'}?`)) return;
    const payload = { scope, scope_id: marketCentre, scope_name: marketCentres.find((item) => item.id === marketCentre)?.name };
    const response = await fetch('/api/reports/kwhomes-email/send-test-real', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(payload) });
    const body = await response.json();
    if (!response.ok) { setMessage(body.error ?? 'Real test send failed.'); return; }
    setPreview(body);
    setMessage(`Real test email sent to ${body.recipients?.[0] ?? 'Ronald'}.`);
    void refreshAdminData();
  };

  const runManualDryRun = async () => {
    const payload = { scope, scope_id: marketCentre, scope_name: marketCentres.find((item) => item.id === marketCentre)?.name, send_type: 'MANUAL' };
    const response = await fetch('/api/reports/kwhomes-email/send-dry-run', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(payload) });
    const body = await response.json();
    setMessage(response.ok ? 'Manual report dry run recorded. No email was delivered.' : (body.error ?? 'Manual dry run failed.'));
    if (response.ok) { setPreview(body); void refreshAdminData(); }
  };

  const simulateSchedule = async () => {
    const response = await fetch('/api/reports/kwhomes-email/schedule-simulate', { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify({}) });
    const body = await response.json();
    setMessage(response.ok ? `Schedule simulation completed: ${body.results?.length ?? 0} dry runs.` : (body.error ?? 'Schedule simulation failed.'));
    if (response.ok) void refreshAdminData();
  };

  const manageScope = (scopeType: "REGION" | "MARKET_CENTRE", scopeId: string) => {
    setScope(scopeType);
    setMarketCentre(scopeType === "MARKET_CENTRE" ? scopeId : "");
    document.getElementById("scheduled-report-setup")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const loadManualPreview = async () => {
    if (manualScope === "MARKET_CENTRE" && !manualMarketCentre) {
      setManualPreview(null);
      setManualMessage("Select a Market Centre before generating a preview.");
      return;
    }
    const query = new URLSearchParams({ scope: manualScope, period_mode: "MANUAL_MTD", _preview: `${Date.now()}` });
    if (manualScope === "MARKET_CENTRE") {
      query.set("scope_id", manualMarketCentre);
      query.set("scope_name", marketCentres.find((item) => item.id === manualMarketCentre)?.name ?? "");
    }
    if (manualRecipients.length > 0) query.set("associate_ids", manualRecipients.join(","));
    const response = await fetch(`/api/reports/kwhomes-email/preview?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    const body = await response.json();
    if (!response.ok) {
      setManualMessage(body.error ?? "Unable to build report.");
      return;
    }
    setManualPreview(body);
    setManualMessage("Preview generated locally.");
  };

  const sendManualTestToRonald = async () => {
    if (!window.confirm("Send the Current Report test email to ronald.vanscheltema@kwsa.co.za?")) return;
    const payload = {
      scope: manualScope,
      scope_id: manualMarketCentre,
      scope_name: marketCentres.find((item) => item.id === manualMarketCentre)?.name,
      period_mode: "MANUAL_MTD",
    };
    const response = await fetch("/api/reports/kwhomes-email/send-test-real", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    });
    const body = await response.json();
    if (!response.ok) {
      setManualMessage(body.error ?? "Real test send failed.");
      return;
    }
    setManualPreview(body);
    setManualMessage(`Real test email sent to ${body.recipients?.[0] ?? "Ronald"}.`);
    void refreshAdminData();
  };

  const sendManualCurrentReport = async () => {
    const payload = {
      scope: manualScope,
      scope_id: manualMarketCentre,
      scope_name: marketCentres.find((item) => item.id === manualMarketCentre)?.name,
      send_type: "MANUAL",
      period_mode: "MANUAL_MTD",
      associate_ids: manualRecipients,
    };
    const response = await fetch("/api/reports/kwhomes-email/send-manual-real", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    });
    const body = await response.json();
    setManualMessage(response.ok ? `Current report sent to ${body.recipients?.length ?? 0} recipient(s).` : (body.error ?? "Manual send failed."));
    if (response.ok) {
      setManualPreview(body);
      void refreshAdminData();
    }
  };

  const visibleHistory = history.filter((item) => (!historyScope || item.scope_type === historyScope) && (!historyType || item.send_type === historyType) && (!historyStatus || item.status === historyStatus));
  const configuredScope = scope === 'REGION' ? 'KWSA' : marketCentre;
  const currentConfig = configs.find((item) => item.scope_type === scope && item.scope_id === configuredScope);
  const savedRecipientIds = (currentConfig?.recipients ?? []).map((recipient) => recipient.id).sort();
  const selectedRecipientIds = [...selectedRecipients].sort();
  const hasUnsavedChanges = selectedRecipientIds.join(',') !== savedRecipientIds.join(',');
  const statusLabel = hasUnsavedChanges ? 'Unsaved Changes' : currentConfig?.enabled ? 'Configured' : currentConfig ? 'Disabled' : 'Not Configured';
  const regionConfig = configs.find((item) => item.scope_type === "REGION" && item.scope_id === "KWSA");
  const overviewRows: Array<{ key: string; label: string; scopeType: "REGION" | "MARKET_CENTRE"; scopeId: string; config: typeof regionConfig }> = [
    { key: "REGION", label: "KWSA Region", scopeType: "REGION", scopeId: "KWSA", config: regionConfig },
    ...marketCentres.map((mc) => ({
      key: mc.id,
      label: mc.name,
      scopeType: "MARKET_CENTRE" as const,
      scopeId: mc.id,
      config: configs.find((item) => item.scope_type === "MARKET_CENTRE" && item.scope_id === mc.id),
    })),
  ];

  return (
    <div className="space-y-4">
      <section className="surface-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Configuration Overview</h2>
            <p className="mt-1 text-sm text-slate-500">Weekly report readiness at a glance — Monday · 07:00 · Africa/Johannesburg.</p>
          </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {overviewRows.map((row) => {
            const status = manualConfigStatus(row.config);
            const recipientCount = row.config?.recipients.length ?? 0;
            return (
              <div key={row.key} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2.5">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-800">{row.label}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        status === "Configured"
                          ? "bg-emerald-50 text-emerald-700"
                          : status === "Disabled"
                            ? "bg-amber-50 text-amber-700"
                            : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {status}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {recipientCount} recipient{recipientCount === 1 ? "" : "s"}
                    </span>
                    <span className="text-[11px] text-slate-400">Monday · 07:00</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => manageScope(row.scopeType, row.scopeId)}
                  className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Manage
                </button>
              </div>
            );
          })}
        </div>
      </section>
      <section id="scheduled-report-setup" className="surface-card border-t-4 border-red-500 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Email Reports
            </h2>
            <p className="mt-1 text-sm text-slate-500">Automated analytics distribution and report management.</p>
          </div>
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            Ronald only
          </span>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-4"><div className="rounded-md bg-slate-50 px-3 py-2"><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Schedule</div><div className="text-sm font-semibold text-slate-800">Monday · 07:00</div></div><div className="rounded-md bg-slate-50 px-3 py-2"><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Timezone</div><div className="text-sm font-semibold text-slate-800">Africa/Johannesburg</div></div><div className="rounded-md bg-slate-50 px-3 py-2"><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Transport</div><div className="text-sm font-semibold text-slate-800">support@kwsa.co.za</div></div><div className="rounded-md bg-slate-50 px-3 py-2"><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Access</div><div className="text-sm font-semibold text-slate-800">Ronald only</div></div></div>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Scope
            </span>
            <select
              value={scope}
              onChange={(event) =>
                setScope(event.target.value as "REGION" | "MARKET_CENTRE")
              }
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
            >
              <option value="REGION">KWSA Region</option>
              <option value="MARKET_CENTRE">Market Centre</option>
            </select>
          </label>
          {scope === "MARKET_CENTRE" && (
            <label className="text-sm">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Market Centre
              </span>
              <select
                value={marketCentre}
                onChange={(event) => setMarketCentre(event.target.value)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
              >
                <option value="">Select Market Centre</option>
                {marketCentres.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <button
            type="button"
            onClick={() => void saveConfig()}
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700"
          >
            Save Configuration
          </button>
          <button
            type="button"
            onClick={() => void loadPreview()}
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-slate-800 px-4 text-sm font-semibold text-white"
          >
            Preview Report
          </button>
          <button
            type="button"
            onClick={() => void sendRealTest()}
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-red-500 px-4 text-sm font-semibold text-red-700"
          >
            Send Test to Ronald
          </button>
          <button type="button" onClick={() => setConfirmAction(scope)} className="flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-red-600 px-4 text-sm font-semibold text-white">Send Live Report</button>
          <button type="button" onClick={() => void simulateSchedule()} className="flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700">Simulate Monday</button>
        </div>
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Active recipients
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedRecipients.map((id) => {
              const recipient = associates.find((item) => item.id === id);
              return recipient ? (
                <button
                  key={id}
                  type="button"
                  onClick={() =>
                    setSelectedRecipients((current) =>
                      current.filter((value) => value !== id),
                    )
                  }
                  className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs text-red-700"
                >
                  {recipient.name} x
                </button>
              ) : null;
            })}
          </div>
          <select
            value=""
            onChange={(event) => {
              if (event.target.value)
                setSelectedRecipients((current) =>
                  current.includes(event.target.value)
                    ? current
                    : [...current, event.target.value],
                );
            }}
            className="mt-3 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">Add active associate...</option>
            {associates
              .filter((item) => !selectedRecipients.includes(item.id))
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} | {item.job_title ?? "Associate"} | {item.email}
                </option>
              ))}
          </select>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm"><span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusLabel === 'Configured' ? 'bg-emerald-50 text-emerald-700' : statusLabel === 'Unsaved Changes' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{statusLabel}</span><span className="text-slate-500">{selectedRecipients.length} selected recipient{selectedRecipients.length === 1 ? '' : 's'} | Monday 07:00</span></div>
        {message && <p className="mt-4 text-sm text-slate-600">{message}</p>}
      </section>
      {preview && (
        <section className="surface-card overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Preview</p><h3 className="font-semibold text-slate-800">
                {preview.subject}
              </h3>
              <p className="text-xs text-slate-500">
                Test recipient: {preview.recipients.join(", ")}
              </p>
            </div>
            <div className="flex rounded-md border border-slate-200 p-0.5"><button type="button" onClick={() => setPreviewMode('desktop')} className={`px-2.5 py-1 text-xs font-semibold ${previewMode === 'desktop' ? 'rounded bg-slate-800 text-white' : 'text-slate-500'}`}>Desktop</button><button type="button" onClick={() => setPreviewMode('mobile')} className={`px-2.5 py-1 text-xs font-semibold ${previewMode === 'mobile' ? 'rounded bg-slate-800 text-white' : 'text-slate-500'}`}>Mobile</button></div>
          </div>
          <iframe
            title="KWHomes email preview"
            srcDoc={preview.html}
            className={`mx-auto block h-[720px] bg-slate-100 ${previewMode === 'mobile' ? 'w-[390px] max-w-full' : 'w-full'}`}
          />
        </section>
      )}
      <section className="surface-card border-t-4 border-slate-800 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Manual Current Report</h2>
            <p className="mt-1 text-sm text-slate-500">Send a fresh KWHomes Analytics Current Month Digest mid-week, independent of the Monday schedule.</p>
          </div>
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Ronald only</span>
        </div>
        <div className="mt-3 inline-block rounded-md bg-slate-50 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Reporting period</div>
          <div className="text-sm font-semibold text-slate-800">
            {manualPeriod.startLabel} – {manualPeriod.endLabel}
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Scope</span>
            <select
              value={manualScope}
              onChange={(event) => setManualScope(event.target.value as "REGION" | "MARKET_CENTRE")}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
            >
              <option value="REGION">KWSA Region</option>
              <option value="MARKET_CENTRE">Market Centre</option>
            </select>
          </label>
          {manualScope === "MARKET_CENTRE" && (
            <label className="text-sm">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Market Centre</span>
              <select
                value={manualMarketCentre}
                onChange={(event) => setManualMarketCentre(event.target.value)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2"
              >
                <option value="">Select Market Centre</option>
                {marketCentres.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            type="button"
            onClick={() => void loadManualPreview()}
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-slate-800 px-4 text-sm font-semibold text-white"
          >
            Preview Current Report
          </button>
          <button
            type="button"
            onClick={() => void sendManualTestToRonald()}
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-red-500 px-4 text-sm font-semibold text-red-700"
          >
            Send Current Test
          </button>
          <button
            type="button"
            onClick={() => {
              if (manualRecipients.length === 0) {
                setManualMessage("Select at least one temporary recipient before sending.");
                return;
              }
              setManualConfirmOpen(true);
            }}
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-red-600 px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Send Current Report
          </button>
        </div>
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Temporary recipients (this send only)</p>
          <div className="flex flex-wrap gap-2">
            {manualRecipients.map((id) => {
              const recipient = manualAssociates.find((item) => item.id === id);
              return recipient ? (
                <button
                  key={id}
                  type="button"
                  onClick={() => setManualRecipients((current) => current.filter((value) => value !== id))}
                  className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs text-red-700"
                >
                  {recipient.name} x
                </button>
              ) : null;
            })}
          </div>
          <select
            value=""
            onChange={(event) => {
              if (event.target.value)
                setManualRecipients((current) =>
                  current.includes(event.target.value) ? current : [...current, event.target.value],
                );
            }}
            className="mt-3 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">Add active associate...</option>
            {manualAssociates
              .filter((item) => !manualRecipients.includes(item.id))
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} | {item.job_title ?? "Associate"} | {item.email}
                </option>
              ))}
          </select>
        </div>
        {manualMessage && <p className="mt-4 text-sm text-slate-600">{manualMessage}</p>}
        {manualPreview && (
          <div className="mt-5 overflow-hidden rounded-md border border-slate-200">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Preview</p>
                <h3 className="font-semibold text-slate-800">{manualPreview.subject}</h3>
                <p className="text-xs text-slate-500">
                  Recipients: {manualPreview.recipients.length > 0 ? manualPreview.recipients.join(", ") : "None selected"}
                </p>
              </div>
              <div className="flex rounded-md border border-slate-200 p-0.5">
                <button
                  type="button"
                  onClick={() => setManualPreviewMode("desktop")}
                  className={`px-2.5 py-1 text-xs font-semibold ${manualPreviewMode === "desktop" ? "rounded bg-slate-800 text-white" : "text-slate-500"}`}
                >
                  Desktop
                </button>
                <button
                  type="button"
                  onClick={() => setManualPreviewMode("mobile")}
                  className={`px-2.5 py-1 text-xs font-semibold ${manualPreviewMode === "mobile" ? "rounded bg-slate-800 text-white" : "text-slate-500"}`}
                >
                  Mobile
                </button>
              </div>
            </div>
            <iframe
              title="KWHomes manual report preview"
              srcDoc={manualPreview.html}
              className={`mx-auto block h-[720px] bg-slate-100 ${manualPreviewMode === "mobile" ? "w-[390px] max-w-full" : "w-full"}`}
            />
          </div>
        )}
      </section>
      <section className="surface-card p-5"><div className="flex flex-wrap items-center justify-between gap-3"><h3 className="font-semibold text-slate-800">Send history</h3><div className="flex flex-wrap gap-2"><select value={historyScope} onChange={(event) => setHistoryScope(event.target.value)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"><option value="">All scopes</option><option value="REGION">Region</option><option value="MARKET_CENTRE">Market Centre</option></select><select value={historyType} onChange={(event) => setHistoryType(event.target.value)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"><option value="">All types</option><option value="TEST">Test</option><option value="MANUAL">Manual</option><option value="SCHEDULED">Scheduled</option></select><select value={historyStatus} onChange={(event) => setHistoryStatus(event.target.value)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"><option value="">All statuses</option><option value="Dry Run">Dry Run</option><option value="Failed">Failed</option></select></div></div><div className="mt-3 overflow-x-auto"><table className="min-w-full text-left text-xs"><thead><tr className="border-b border-slate-200 text-slate-500"><th className="p-2">Date / Time</th><th className="p-2">Scope</th><th className="p-2">Recipients</th><th className="p-2">Type</th><th className="p-2">Period</th><th className="p-2">Status</th></tr></thead><tbody>{visibleHistory.length === 0 ? <tr><td colSpan={6} className="p-3 text-slate-500">No local runs recorded.</td></tr> : visibleHistory.map((item) => <tr key={`${item.created_at}-${item.scope_id}`} className="border-b border-slate-100"><td className="p-2">{new Date(item.created_at).toLocaleString()}</td><td className="p-2">{item.scope_id}</td><td className="p-2">{item.recipient_count}</td><td className="p-2">{item.send_type}</td><td className="p-2">{item.report_start} - {item.report_end}</td><td className="p-2 font-semibold">{item.status}</td></tr>)}</tbody></table></div></section>
      {confirmAction && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"><div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl"><h3 className="text-lg font-semibold text-slate-800">Send {confirmAction === 'REGION' ? 'KWSA Regional' : 'Selected Market Centre'} report?</h3><p className="mt-2 text-sm text-slate-600">This local phase will create a Dry Run only. Recipients: {selectedRecipients.length}. Period: previous Monday through Sunday.</p><div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setConfirmAction(null)} className="rounded-md border border-slate-300 px-3 py-2 text-sm">Cancel</button><button type="button" onClick={() => { setConfirmAction(null); void runManualDryRun(); }} className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white">Run dry run</button></div></div></div>}
      {manualConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-800">Send Current KWHomes Analytics Report?</h3>
            <p className="mt-2 text-sm text-slate-600">
              Scope: {manualScope === "REGION" ? "KWSA Region" : marketCentres.find((item) => item.id === manualMarketCentre)?.name ?? "Selected Market Centre"}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Reporting period: {manualPeriod.startLabel} – {manualPeriod.endLabel}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Recipients: {manualRecipients.length === 0 ? "None selected" : manualRecipients.map((id) => {
                const recipient = manualAssociates.find((item) => item.id === id);
                return recipient ? `${recipient.name} (${recipient.email})` : id;
              }).join(", ")}
            </p>
            <p className="mt-2 text-sm text-slate-600">This will send the Current KWHomes Analytics Report to the selected recipient(s). This will not change the saved Monday configuration.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setManualConfirmOpen(false)} className="rounded-md border border-slate-300 px-3 py-2 text-sm">Cancel</button>
              <button
                type="button"
                onClick={() => {
                  setManualConfirmOpen(false);
                  void sendManualCurrentReport();
                }}
                className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white"
              >
                Send Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return <ReportKpiCard label={label} value={value} icon={icon} />;
}

function classNames(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

type SharedAnalyticsFilters = {
  dateRange: string;
  startDate: string;
  endDate: string;
  marketCentreId: string;
  agentKwuid: string;
  pageType: string;
  trafficSource: string;
  deviceType: string;
};

type AgentTableRow = {
  kwuid: string;
  agent: string;
  market_centre: string;
  unique_visitors: number | string;
  sessions: number | string;
  page_views: number | string;
  listing_views: number | string;
  agent_site_views: number | string;
  vcard_views: number | string;
  leads: number | string;
  whatsapp_clicks: number | string;
  call_clicks: number | string;
  email_clicks: number | string;
  finance_clicks: number | string;
};

type MarketCentreTableRow = {
  market_centre_id: string;
  market_centre: string;
  unique_visitors: number | string;
  sessions: number | string;
  page_views: number | string;
  listing_views: number | string;
  agent_site_views: number | string;
  vcard_views: number | string;
  leads: number | string;
  whatsapp_clicks: number | string;
  finance_clicks: number | string;
};

type ListingTableRow = {
  listing_number: string;
  suburb: string;
  presenting_agent: string;
  kwuid: string;
  market_centre: string;
  views: number | string;
  unique_visitors: number | string;
  leads: number | string;
  whatsapp_clicks: number | string;
};

type VcardTableRow = {
  kwuid: string;
  agent: string;
  market_centre: string;
  vcard_views: number | string;
  unique_visitors: number | string;
  save_contact_clicks: number | string;
  share_clicks: number | string;
  whatsapp_clicks: number | string;
  call_clicks: number | string;
  email_clicks: number | string;
};

type LeadsResponse = {
  total: number;
  by_day: Array<{ date: string; leads: number }>;
  by_agent: Array<{ agent: string; kwuid: string; market_centre: string; leads: number | string }>;
  by_market_centre: Array<{ market_centre: string; market_centre_id: string; leads: number | string }>;
  recent: Array<{ id: string; lead_type: string | null; lead_source: string | null; agent: string; market_centre: string; listing_number: string | null; page_url: string | null; created_at: string }>;
  source: string;
};

type TrafficResponse = {
  human: { unique_visitors: number; sessions: number; page_views: number; pages_per_session: number };
  traffic_over_time: Array<{ date: string; visitors: number; sessions: number; page_views: number }>;
  traffic_sources: Array<{ source: string; sessions: number }>;
  device_split: Array<{ device: string; sessions: number }>;
  top_page_types: Array<{ page_type: string; events: number }>;
  top_entry_pages: Array<{ page_path: string; sessions: number }>;
  bot_diagnostics: {
    raw_events: number;
    bot_events: number;
    bot_sessions: number;
    bot_visitors: number;
    bot_pct_of_raw: number;
    last_observed: string | null;
    top_categories: Array<{ category: string; events: number; sessions: number }>;
  };
};

export function buildKwhomesTabParams(filters: SharedAnalyticsFilters): URLSearchParams {
  const params = new URLSearchParams({ range: filters.dateRange });
  if (filters.dateRange === "custom" && filters.startDate) params.set("start_date", filters.startDate);
  if (filters.dateRange === "custom" && filters.endDate) params.set("end_date", filters.endDate);
  if (filters.marketCentreId) params.set("market_centre_id", filters.marketCentreId);
  if (filters.agentKwuid) params.set("agent_kwuid", filters.agentKwuid);
  if (filters.pageType) params.set("page_type", filters.pageType);
  if (filters.trafficSource) params.set("traffic_source", filters.trafficSource);
  if (filters.deviceType) params.set("device_type", filters.deviceType);
  return params;
}

export function buildKwhomesRequestHeaders(token: string | null, activeContextId: string): Record<string, string> {
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(activeContextId ? { "X-Active-Context": activeContextId } : {}),
  };
}

export function agentKwuidForContext(agentKwuid: string, selectedContextId: string, activeContextId: string): string {
  return selectedContextId === activeContextId ? agentKwuid : "";
}

// Shared fetch-on-activate hook so each analytics tab only loads its dataset while visible.
function useKwhomesTabData<T>(endpoint: string, active: boolean, filters: SharedAnalyticsFilters, activeContextId: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    const token = localStorage.getItem("kwsa_auth_token");
    const params = buildKwhomesTabParams(filters);
    setLoading(true);
    fetch(`${endpoint}?${params.toString()}`, {
      headers: buildKwhomesRequestHeaders(token, activeContextId),
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error((await res.json().catch(() => ({ error: "Failed to load data." }))).error ?? "Failed to load data.");
        }
        return res.json() as Promise<T>;
      })
      .then((body) => {
        setData(body);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load data.");
        setData(null);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, endpoint, activeContextId, filters.dateRange, filters.startDate, filters.endDate, filters.marketCentreId, filters.agentKwuid, filters.pageType, filters.trafficSource, filters.deviceType]);

  return { data, loading, error };
}

type TableColumn<T> = {
  key: string;
  label: string;
  align?: "left" | "right";
  render?: (row: T) => React.ReactNode;
  sortValue?: (row: T) => number | string;
};

// Generic searchable / sortable / paginated table reused across the Agents, Market Centres,
// Properties, VCards, and Leads tabs so each tab shares identical interaction behaviour.
function AnalyticsTable<T extends Record<string, unknown>>({
  rows,
  columns,
  searchKeys,
  pageSize = 15,
  emptyMessage,
  initialSortKey,
}: {
  rows: T[];
  columns: Array<TableColumn<T>>;
  searchKeys?: string[];
  pageSize?: number;
  emptyMessage?: string;
  initialSortKey?: string;
}) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(initialSortKey ?? columns[0]?.key ?? "");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    const keys = searchKeys ?? columns.map((col) => col.key);
    return rows.filter((row) => keys.some((key) => String(row[key] ?? "").toLowerCase().includes(q)));
  }, [rows, search, searchKeys, columns]);

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    const list = [...filtered];
    list.sort((a, b) => {
      const av = col?.sortValue ? col.sortValue(a) : a[sortKey];
      const bv = col?.sortValue ? col.sortValue(b) : b[sortKey];
      const an = Number(av);
      const bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn) && av !== "" && bv !== "") {
        return sortDir === "asc" ? an - bn : bn - an;
      }
      return sortDir === "asc"
        ? String(av ?? "").localeCompare(String(bv ?? ""))
        : String(bv ?? "").localeCompare(String(av ?? ""));
    });
    return list;
  }, [filtered, sortKey, sortDir, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const clampedPage = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
    setPage(0);
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <input
          type="text"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          placeholder="Search..."
          className="w-full max-w-xs rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
        />
        <span className="text-xs text-slate-500">
          {sorted.length} result{sorted.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-slate-600">
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col.key)}
                  className={classNames(
                    "cursor-pointer select-none py-2 pr-3 hover:text-slate-900",
                    col.align === "right" ? "text-right" : "text-left",
                  )}
                >
                  {col.label}
                  {sortKey === col.key ? (sortDir === "asc" ? " \u25b2" : " \u25bc") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-3 text-slate-500">
                  {emptyMessage ?? "No data available."}
                </td>
              </tr>
            ) : (
              pageRows.map((row, index) => (
                <tr key={index} className="border-b border-slate-100">
                  {columns.map((col) => (
                    <td key={col.key} className={classNames("py-2 pr-3", col.align === "right" ? "text-right" : "")}>
                      {col.render ? col.render(row) : String(row[col.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs text-slate-500">
          <button
            type="button"
            disabled={clampedPage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-md border border-slate-200 px-2 py-1 disabled:opacity-40"
          >
            Prev
          </button>
          <span>
            Page {clampedPage + 1} of {totalPages}
          </span>
          <button
            type="button"
            disabled={clampedPage >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            className="rounded-md border border-slate-200 px-2 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// Searchable, debounced Agent filter: canonical active agent names only, kwuid kept internally as the value.
function AgentFilterCombobox({
  token,
  activeContextId,
  marketCentreId,
  value,
  valueLabel,
  onSelect,
}: {
  token: string | null;
  activeContextId: string;
  marketCentreId: string;
  value: string;
  valueLabel: string;
  onSelect: (kwuid: string, name: string) => void;
}) {
  const [query, setQuery] = useState(valueLabel);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Array<{ kwuid: string; name: string; market_centre: string | null }>>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(valueLabel);
  }, [valueLabel]);

  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => {
      const params = new URLSearchParams();
      if (query.trim() && query.trim() !== valueLabel) params.set("q", query.trim());
      if (marketCentreId) params.set("market_centre_id", marketCentreId);
      setLoading(true);
      fetch(`/api/reports/kwhomes/agent-options?${params.toString()}`, {
        headers: buildKwhomesRequestHeaders(token, activeContextId),
      })
        .then((res) => res.json())
        .then((body) => setOptions(body.rows ?? []))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, activeContextId, marketCentreId, token]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        placeholder="Search agent name..."
        className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onSelect("", "");
            setQuery("");
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600"
          aria-label="Clear agent filter"
        >
          &times;
        </button>
      )}
      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {loading ? (
            <div className="px-3 py-2 text-xs text-slate-500">Searching...</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">No matching active agents.</div>
          ) : (
            options.map((option) => (
              <button
                key={option.kwuid}
                type="button"
                onClick={() => {
                  onSelect(option.kwuid, option.name);
                  setQuery(option.name);
                  setOpen(false);
                }}
                className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-slate-50"
              >
                {option.name}
                {option.market_centre ? <span className="ml-2 text-xs text-slate-400">{option.market_centre}</span> : null}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const KWHOMES_BASE_URL = "https://kwhomes.co.za";

function isValidKwuid(value: unknown): value is string {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return false;
  const lowered = text.toLowerCase();
  return lowered !== "unknown" && lowered !== "null" && lowered !== "undefined";
}

function isValidKwhomesPath(value: unknown): value is string {
  const text = typeof value === "string" ? value.trim() : "";
  return text.startsWith("/");
}

// Subtle link styling for identifiers that open their real KWHomes destination in a new tab.
function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline decoration-transparent underline-offset-2 hover:decoration-current"
    >
      {children}
    </a>
  );
}

function csvCell(value: unknown): string {
  const str = value === null || value === undefined ? "" : String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function csvSection(rows: Array<Array<unknown>>): string {
  return rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
}

function downloadCsv(filename: string, sections: Array<Array<Array<unknown>>>) {
  const csv = sections.map(csvSection).join("\r\n\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function csvStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function ExportCsvButton({ onExport }: { onExport: () => void }) {
  return (
    <button
      type="button"
      onClick={onExport}
      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
    >
      Export CSV
    </button>
  );
}

export default function KWHomesAnalyticsReport() {
  const { user, isRegionalAdminAccount, isOfficeAdmin, activeContext } = useAuth();
  const activeContextId = activeContext?.id ?? "";
  const canViewAnalytics = Boolean(activeContext?.associateId);
  const activeRole = (activeContext?.role ?? '').trim().toLowerCase().replace(/[_\s]+/g, ' ');
  const canSelectMarketCentre = isRegionalAdminAccount || isOfficeAdmin;
  const canSelectAgent = canSelectMarketCentre || activeRole === 'lead agent' || activeRole === 'team admin';
  const canConfigureEmailReports =
    user?.email.trim().toLowerCase() === "ronald.vanscheltema@kwsa.co.za";
  const [activeTab, setActiveTab] = useState<
    | "Overview"
    | "Agents"
    | "Market Centres"
    | "Properties"
    | "VCards"
    | "Leads"
    | "Traffic"
    | "Email Reports"
  >("Overview");
  const [dateRange, setDateRange] = useState("30d");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [marketCentreId, setMarketCentreId] = useState("");
  const [agentKwuid, setAgentKwuid] = useState("");
  const [agentName, setAgentName] = useState("");
  const [agentContextId, setAgentContextId] = useState(activeContextId);
  const [pageType, setPageType] = useState("");
  const [trafficSource, setTrafficSource] = useState("");
  const [deviceType, setDeviceType] = useState("");
  const [agentFilterResetKey, setAgentFilterResetKey] = useState(0);
  const [data, setData] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const filterMarketCentreId = isRegionalAdminAccount
    ? marketCentreId
    : isOfficeAdmin
      ? activeContext?.marketCenterId ?? ''
      : '';
  const effectiveAgentKwuid = agentKwuidForContext(agentKwuid, agentContextId, activeContextId);
  const effectiveAgentName = agentContextId === activeContextId ? agentName : "";

  useEffect(() => {
    if (agentContextId === activeContextId) return;
    setAgentKwuid("");
    setAgentName("");
    setAgentContextId(activeContextId);
    setAgentFilterResetKey((key) => key + 1);
  }, [activeContextId, agentContextId]);

  const sharedFilters: SharedAnalyticsFilters = {
    dateRange,
    startDate,
    endDate,
    marketCentreId: filterMarketCentreId,
    agentKwuid: effectiveAgentKwuid,
    pageType,
    trafficSource,
    deviceType,
  };
  const agentsTab = useKwhomesTabData<{ rows: AgentTableRow[] }>(
    "/api/reports/kwhomes/agents",
    canViewAnalytics && activeTab === "Agents",
    sharedFilters,
    activeContextId,
  );
  const marketCentresTab = useKwhomesTabData<{ rows: MarketCentreTableRow[] }>(
    "/api/reports/kwhomes/market-centres",
    canViewAnalytics && activeTab === "Market Centres",
    sharedFilters,
    activeContextId,
  );
  const propertiesTab = useKwhomesTabData<{ rows: ListingTableRow[] }>(
    "/api/reports/kwhomes/listings",
    canViewAnalytics && activeTab === "Properties",
    sharedFilters,
    activeContextId,
  );
  const vcardsTab = useKwhomesTabData<{ rows: VcardTableRow[] }>(
    "/api/reports/kwhomes/vcards",
    canViewAnalytics && activeTab === "VCards",
    sharedFilters,
    activeContextId,
  );
  const leadsTab = useKwhomesTabData<LeadsResponse>(
    "/api/reports/kwhomes/leads",
    canViewAnalytics && activeTab === "Leads",
    sharedFilters,
    activeContextId,
  );
  const trafficTab = useKwhomesTabData<TrafficResponse>(
    "/api/reports/kwhomes/traffic",
    canViewAnalytics && activeTab === "Traffic",
    sharedFilters,
    activeContextId,
  );

  useEffect(() => {
    if (!canViewAnalytics) {
      setLoading(false);
      setData(null);
      return;
    }
    const token = localStorage.getItem("kwsa_auth_token");
    const params = new URLSearchParams({ range: dateRange });
    if (dateRange === "custom" && startDate)
      params.set("start_date", startDate);
    if (dateRange === "custom" && endDate) params.set("end_date", endDate);
    if (filterMarketCentreId) params.set("market_centre_id", filterMarketCentreId);
    if (effectiveAgentKwuid) params.set("agent_kwuid", effectiveAgentKwuid);
    if (pageType) params.set("page_type", pageType);
    if (trafficSource) params.set("traffic_source", trafficSource);
    if (deviceType) params.set("device_type", deviceType);

    setLoading(true);
    fetch(`/api/reports/kwhomes/overview?${params.toString()}`, {
      headers: buildKwhomesRequestHeaders(token, activeContextId),
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(
            (
              await res
                .json()
                .catch(() => ({ error: "Failed to load KWHomes Analytics." }))
            ).error ?? "Failed to load KWHomes Analytics.",
          );
        }
        return res.json() as Promise<SummaryResponse>;
      })
      .then((result) => {
        setData(result);
        setError(null);
      })
      .catch((err) => {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load KWHomes Analytics.",
        );
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [
    dateRange,
    startDate,
    endDate,
    marketCentreId,
    effectiveAgentKwuid,
    pageType,
    trafficSource,
    deviceType,
    canViewAnalytics,
    filterMarketCentreId,
    activeContextId,
  ]);

  const trafficOverTime = useMemo(
    () => data?.charts.traffic_over_time ?? [],
    [data],
  );
  const maxTraffic = useMemo(
    () =>
      Math.max(
        1,
        ...trafficOverTime.flatMap((point) => [
          point.visitors,
          point.page_views,
          point.listing_views,
          point.leads,
        ]),
      ),
    [trafficOverTime],
  );
  const trafficSeries = [
    { key: "visitors", label: "Visitors", color: "bg-red-300" },
    { key: "page_views", label: "Page Views", color: "bg-slate-300" },
    { key: "listing_views", label: "Listing Views", color: "bg-sky-300" },
    { key: "leads", label: "Leads", color: "bg-emerald-300" },
  ] as const;

  const tabOptions: Array<
    | "Overview"
    | "Agents"
    | "Market Centres"
    | "Properties"
    | "VCards"
    | "Leads"
    | "Traffic"
    | "Email Reports"
  > = [
    "Overview",
    "Agents",
    "Market Centres",
    "Properties",
    "VCards",
    "Leads",
    "Traffic",
    ...(canConfigureEmailReports ? ["Email Reports" as const] : []),
  ];

  // Shared filter state persists across tabs, so Clear Filters must live in the common filter bar.
  const isFiltersDefault =
    dateRange === "30d" &&
    startDate === "" &&
    endDate === "" &&
    marketCentreId === "" &&
    agentKwuid === "" &&
    pageType === "" &&
    trafficSource === "" &&
    deviceType === "";

  const clearFilters = () => {
    setDateRange("30d");
    setStartDate("");
    setEndDate("");
    setMarketCentreId("");
    setAgentKwuid("");
    setAgentName("");
    setAgentContextId(activeContextId);
    setPageType("");
    setTrafficSource("");
    setDeviceType("");
    setAgentFilterResetKey((key) => key + 1);
  };

  const renderOverview = () => {
    if (loading) {
      return (
        <ReportState type="loading" message="Loading KWHomes analytics..." />
      );
    }

    if (error) {
      return <ReportState type="error" message={error} />;
    }

    if (!data) {
      return (
        <ReportState
          type="empty"
          message="No analytics data is available for this selection."
        />
      );
    }

    const metrics = data.metrics;

    const exportOverviewCsv = () => {
      downloadCsv(`kwhomes-overview-${csvStamp()}.csv`, [
        [["Summary Metrics"], ["Metric", "Value"], ...Object.entries(metrics).map(([key, value]) => [key, value])],
        [
          ["Top Agent Sites"],
          ["Agent", "Market Centre", "Agent Site Views", "Unique Visitors", "Page Views", "Listing Views", "VCard Views", "Leads", "WhatsApp", "Finance"],
          ...data.tables.top_agents.map((row) => [row.agent, row.market_centre, row.agent_site_views, row.unique_visitors, row.page_views, row.listing_views, row.vcard_views, row.leads, row.whatsapp_clicks, row.finance_clicks]),
        ],
        [
          ["Top Market Centres"],
          ["Market Centre", "Unique Visitors", "Page Views", "Listing Views", "Leads", "Finance"],
          ...data.tables.top_market_centres.map((row) => [row.market_centre, row.unique_visitors, row.page_views, row.listing_views, row.leads, row.finance_clicks]),
        ],
        [
          ["Most Viewed Listings"],
          ["Listing Number", "Suburb / City", "Presenting Agent", "KWUID", "Market Centre", "Views", "Unique Visitors", "Leads"],
          ...data.tables.most_viewed_listings.map((row) => [row.listing_number, row.suburb, row.presenting_agent, row.kwuid ?? "", row.market_centre, row.views, row.unique_visitors, row.leads]),
        ],
      ]);
    };

    return (
      <>
        <div className="flex justify-end">
          <ExportCsvButton onExport={exportOverviewCsv} />
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <StatCard
            label="Unique Visitors"
            value={String(metrics.unique_visitors)}
            icon={<ReportIcon kind="users" className="h-4 w-4" />}
          />
          <StatCard
            label="Sessions"
            value={String(metrics.sessions)}
            icon={<ReportIcon kind="chart" className="h-4 w-4" />}
          />
          <StatCard
            label="Page Views"
            value={String(metrics.page_views)}
            icon={<ReportIcon kind="gauge" className="h-4 w-4" />}
          />
          <StatCard
            label="Listing Views"
            value={String(metrics.listing_views)}
            icon={<ReportIcon kind="home" className="h-4 w-4" />}
          />
          <StatCard
            label="Agent Site Views"
            value={String(metrics.agent_site_views)}
            icon={<ReportIcon kind="target" className="h-4 w-4" />}
          />
          <StatCard
            label="VCard Views"
            value={String(metrics.vcard_views)}
            icon={<ReportIcon kind="file-check" className="h-4 w-4" />}
          />
          <StatCard
            label="Leads"
            value={String(metrics.leads)}
            icon={<ReportIcon kind="check-circle" className="h-4 w-4" />}
          />
          <StatCard
            label="WhatsApp Clicks"
            value={String(metrics.whatsapp_clicks)}
            icon={<ReportIcon kind="trending-up" className="h-4 w-4" />}
          />
          <StatCard
            label="Call Clicks"
            value={String(metrics.call_clicks)}
            icon={<ReportIcon kind="calendar" className="h-4 w-4" />}
          />
          <StatCard
            label="Email Clicks"
            value={String(metrics.email_clicks)}
            icon={<ReportIcon kind="layers" className="h-4 w-4" />}
          />
          <StatCard
            label="KW Finance Clicks"
            value={String(metrics.finance_clicks)}
            icon={<ReportIcon kind="wallet" className="h-4 w-4" />}
          />
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <section className="surface-card p-4 xl:col-span-2">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
                Traffic Over Time
              </h3>
              <div
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500"
                aria-label="Traffic Over Time legend"
              >
                {trafficSeries.map((series) => (
                  <span
                    key={series.key}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${series.color}`}
                      aria-hidden="true"
                    />
                    {series.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex h-48 items-end gap-2 overflow-visible">
              {trafficOverTime.length === 0 ? (
                <div className="text-sm text-slate-500">
                  No traffic data for this range.
                </div>
              ) : (
                trafficOverTime.map((point) => (
                  <div
                    key={point.date}
                    className="group relative flex flex-1 flex-col items-center gap-2"
                  >
                    <div className="pointer-events-none absolute bottom-12 left-1/2 z-10 hidden w-36 -translate-x-1/2 rounded-md border border-slate-200 bg-white p-2 text-xs shadow-lg group-hover:block group-focus-within:block">
                      <p className="mb-1 font-semibold text-slate-700">
                        {new Date(`${point.date}T00:00:00Z`).toLocaleDateString(
                          undefined,
                          { day: "numeric", month: "short" },
                        )}
                      </p>
                      {trafficSeries.map((series) => (
                        <div
                          key={series.key}
                          className="flex items-center justify-between gap-2 py-0.5 text-slate-600"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className={`h-2 w-2 rounded-full ${series.color}`}
                              aria-hidden="true"
                            />
                            {series.label}
                          </span>
                          <span className="font-semibold text-slate-800">
                            {point[series.key]}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex h-36 w-full items-end justify-center gap-1">
                      <div
                        className="w-1/4 rounded-t bg-red-300"
                        style={{
                          height: `${Math.max(8, (point.visitors / maxTraffic) * 100)}%`,
                        }}
                        aria-label={`Visitors: ${point.visitors}`}
                      />
                      <div
                        className="w-1/4 rounded-t bg-slate-300"
                        style={{
                          height: `${Math.max(8, (point.page_views / maxTraffic) * 100)}%`,
                        }}
                        aria-label={`Page Views: ${point.page_views}`}
                      />
                      <div
                        className="w-1/4 rounded-t bg-sky-300"
                        style={{
                          height: `${Math.max(8, (point.listing_views / maxTraffic) * 100)}%`,
                        }}
                        aria-label={`Listing Views: ${point.listing_views}`}
                      />
                      <div
                        className="w-1/4 rounded-t bg-emerald-300"
                        style={{
                          height: `${Math.max(8, (point.leads / maxTraffic) * 100)}%`,
                        }}
                        aria-label={`Leads: ${point.leads}`}
                      />
                    </div>
                    <span className="text-[10px] text-slate-500">
                      {new Date(`${point.date}T00:00:00Z`).toLocaleDateString(
                        undefined,
                        { month: "short", day: "numeric" },
                      )}
                    </span>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="surface-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
              Traffic Sources
            </h3>
            <div className="space-y-3">
              {data.charts.traffic_sources.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No traffic-source data yet.
                </p>
              ) : (
                data.charts.traffic_sources.map((item) => (
                  <div key={item.source}>
                    <div className="mb-1 flex justify-between text-xs text-slate-600">
                      <span>{item.source}</span>
                      <span>{item.value}</span>
                    </div>
                    <div className="h-2 rounded bg-slate-200">
                      <div
                        className="h-2 rounded bg-red-500"
                        style={{
                          width: `${Math.max(8, (item.value / Math.max(1, ...data.charts.traffic_sources.map((row) => row.value))) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="surface-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
              Device Split
            </h3>
            <div className="space-y-3">
              {data.charts.device_split.length === 0 ? (
                <p className="text-sm text-slate-500">No device data yet.</p>
              ) : (
                data.charts.device_split.map((item) => (
                  <div key={item.device}>
                    <div className="mb-1 flex justify-between text-xs text-slate-600">
                      <span>{item.device}</span>
                      <span>{item.value}</span>
                    </div>
                    <div className="h-2 rounded bg-slate-200">
                      <div
                        className="h-2 rounded bg-sky-500"
                        style={{
                          width: `${Math.max(8, (item.value / Math.max(1, ...data.charts.device_split.map((row) => row.value))) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <section className="surface-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
              Top Agent Sites
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600">
                    <th className="py-2 pr-3">Agent</th>
                    <th className="py-2 pr-3">MC</th>
                    <th className="py-2 pr-3">Agent Site Views</th>
                    <th className="py-2 pr-3">Visitors</th>
                    <th className="py-2 pr-3">Page Views</th>
                    <th className="py-2 pr-3">Listing Views</th>
                    <th className="py-2 pr-3">VCard Views</th>
                    <th className="py-2 pr-3">Leads</th>
                    <th className="py-2 pr-3">WhatsApp</th>
                    <th className="py-2 pr-3">Finance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tables.top_agents.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-3 text-slate-500">
                        No agent data available.
                      </td>
                    </tr>
                  ) : (
                    data.tables.top_agents.map((row) => (
                      <tr
                        key={`${row.kwuid ?? row.agent}-${row.market_centre}`}
                        className="border-b border-slate-100"
                      >
                        <td className="py-2 pr-3">{row.agent}</td>
                        <td className="py-2 pr-3">{row.market_centre}</td>
                        <td className="py-2 pr-3">{row.agent_site_views}</td>
                        <td className="py-2 pr-3">{row.unique_visitors}</td>
                        <td className="py-2 pr-3">{row.page_views}</td>
                        <td className="py-2 pr-3">{row.listing_views}</td>
                        <td className="py-2 pr-3">{row.vcard_views}</td>
                        <td className="py-2 pr-3">{row.leads}</td>
                        <td className="py-2 pr-3">{row.whatsapp_clicks}</td>
                        <td className="py-2 pr-3">{row.finance_clicks}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="surface-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
              Top Market Centres
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600">
                    <th className="py-2 pr-3">Market Centre</th>
                    <th className="py-2 pr-3">Visitors</th>
                    <th className="py-2 pr-3">Page Views</th>
                    <th className="py-2 pr-3">Listing Views</th>
                    <th className="py-2 pr-3">Leads</th>
                    <th className="py-2 pr-3">Finance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tables.top_market_centres.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-3 text-slate-500">
                        No market-centre data available.
                      </td>
                    </tr>
                  ) : (
                    data.tables.top_market_centres.map((row) => (
                      <tr
                        key={row.market_centre_id ?? row.market_centre}
                        className="border-b border-slate-100"
                      >
                        <td className="py-2 pr-3">{row.market_centre}</td>
                        <td className="py-2 pr-3">{row.unique_visitors}</td>
                        <td className="py-2 pr-3">{row.page_views}</td>
                        <td className="py-2 pr-3">{row.listing_views}</td>
                        <td className="py-2 pr-3">{row.leads}</td>
                        <td className="py-2 pr-3">{row.finance_clicks}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <section className="surface-card p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
            Most Viewed Listings
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600">
                  <th className="py-2 pr-3">Listing Number</th>
                  <th className="py-2 pr-3">Suburb / City</th>
                  <th className="py-2 pr-3">Presenting Agent / KWUID</th>
                  <th className="py-2 pr-3">MC</th>
                  <th className="py-2 pr-3">Views</th>
                  <th className="py-2 pr-3">Visitors</th>
                  <th className="py-2 pr-3">Leads</th>
                </tr>
              </thead>
              <tbody>
                {data.tables.most_viewed_listings.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-3 text-slate-500">
                      No listing activity yet.
                    </td>
                  </tr>
                ) : (
                  data.tables.most_viewed_listings.map((row) => (
                    <tr
                      key={`${row.listing_number}-${row.market_centre}`}
                      className="border-b border-slate-100"
                    >
                      <td className="py-2 pr-3">
                        {isValidKwuid(row.kwuid) ? (
                          <ExternalLink href={`${KWHOMES_BASE_URL}/${row.kwuid}/${row.listing_number}`}>{row.listing_number}</ExternalLink>
                        ) : (
                          row.listing_number
                        )}
                      </td>
                      <td className="py-2 pr-3">{row.suburb}</td>
                      <td className="py-2 pr-3">
                        {row.presenting_agent}
                        {row.kwuid ? ` / ${row.kwuid}` : ""}
                      </td>
                      <td className="py-2 pr-3">{row.market_centre}</td>
                      <td className="py-2 pr-3">{row.views}</td>
                      <td className="py-2 pr-3">{row.unique_visitors}</td>
                      <td className="py-2 pr-3">{row.leads}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </>
    );
  };

  const renderAgentsTab = () => {
    if (agentsTab.loading) return <ReportState type="loading" message="Loading Agents analytics..." />;
    if (agentsTab.error) return <ReportState type="error" message={agentsTab.error} />;
    const rows = agentsTab.data?.rows ?? [];
    const totalVisitors = rows.reduce((sum, row) => sum + Number(row.unique_visitors ?? 0), 0);
    const totalAgentSiteViews = rows.reduce((sum, row) => sum + Number(row.agent_site_views ?? 0), 0);
    const exportAgentsCsv = () => {
      downloadCsv(`kwhomes-agents-${csvStamp()}.csv`, [[
        ["Agent", "Market Centre", "Agent Site Views", "Unique Visitors", "Sessions", "Page Views", "Listing Views", "VCard Views", "Leads", "WhatsApp", "Call", "Email", "KW Finance"],
        ...rows.map((row) => [row.agent, row.market_centre, row.agent_site_views, row.unique_visitors, row.sessions, row.page_views, row.listing_views, row.vcard_views, row.leads, row.whatsapp_clicks, row.call_clicks, row.email_clicks, row.finance_clicks]),
      ]]);
    };
    return (
      <>
        <div className="grid gap-3 md:grid-cols-3">
          <StatCard label="Agents with activity" value={String(rows.length)} icon={<ReportIcon kind="users" className="h-4 w-4" />} />
          <StatCard label="Total Agent Site Views" value={totalAgentSiteViews.toLocaleString()} icon={<ReportIcon kind="target" className="h-4 w-4" />} />
          <StatCard label="Total Unique Visitors" value={totalVisitors.toLocaleString()} icon={<ReportIcon kind="chart" className="h-4 w-4" />} />
        </div>
        <section className="surface-card p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Agents</h3>
            <ExportCsvButton onExport={exportAgentsCsv} />
          </div>
          <AnalyticsTable<AgentTableRow>
            rows={rows}
            searchKeys={["agent", "market_centre"]}
            initialSortKey="agent_site_views"
            emptyMessage="No agent activity for this selection."
            columns={[
              { key: "agent", label: "Agent", render: (row) => (isValidKwuid(row.kwuid) ? <ExternalLink href={`${KWHOMES_BASE_URL}/${row.kwuid}`}>{row.agent}</ExternalLink> : row.agent) },
              { key: "market_centre", label: "Market Centre" },
              { key: "agent_site_views", label: "Agent Site Views", align: "right", sortValue: (row) => Number(row.agent_site_views ?? 0) },
              { key: "unique_visitors", label: "Unique Visitors", align: "right", sortValue: (row) => Number(row.unique_visitors ?? 0) },
              { key: "sessions", label: "Sessions", align: "right", sortValue: (row) => Number(row.sessions ?? 0) },
              { key: "page_views", label: "Page Views", align: "right", sortValue: (row) => Number(row.page_views ?? 0) },
              { key: "listing_views", label: "Listing Views", align: "right", sortValue: (row) => Number(row.listing_views ?? 0) },
              { key: "vcard_views", label: "VCard Views", align: "right", sortValue: (row) => Number(row.vcard_views ?? 0) },
              { key: "leads", label: "Leads", align: "right", sortValue: (row) => Number(row.leads ?? 0) },
              { key: "whatsapp_clicks", label: "WhatsApp", align: "right", sortValue: (row) => Number(row.whatsapp_clicks ?? 0) },
              { key: "call_clicks", label: "Call", align: "right", sortValue: (row) => Number(row.call_clicks ?? 0) },
              { key: "email_clicks", label: "Email", align: "right", sortValue: (row) => Number(row.email_clicks ?? 0) },
              { key: "finance_clicks", label: "KW Finance", align: "right", sortValue: (row) => Number(row.finance_clicks ?? 0) },
            ]}
          />
        </section>
      </>
    );
  };

  const renderMarketCentresTab = () => {
    if (marketCentresTab.loading) return <ReportState type="loading" message="Loading Market Centres analytics..." />;
    if (marketCentresTab.error) return <ReportState type="error" message={marketCentresTab.error} />;
    const rows = marketCentresTab.data?.rows ?? [];
    const exportMarketCentresCsv = () => {
      downloadCsv(`kwhomes-market-centres-${csvStamp()}.csv`, [[
        ["Market Centre", "Visitors", "Sessions", "Page Views", "Listing Views", "Agent Site Views", "VCard Views", "Leads", "WhatsApp", "Finance"],
        ...rows.map((row) => [row.market_centre, row.unique_visitors, row.sessions, row.page_views, row.listing_views, row.agent_site_views, row.vcard_views, row.leads, row.whatsapp_clicks, row.finance_clicks]),
      ]]);
    };
    return (
      <section className="surface-card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Market Centres</h3>
          <ExportCsvButton onExport={exportMarketCentresCsv} />
        </div>
        <AnalyticsTable<MarketCentreTableRow>
          rows={rows}
          searchKeys={["market_centre"]}
          initialSortKey="unique_visitors"
          emptyMessage="No active Market Centre activity for this selection."
          columns={[
            { key: "market_centre", label: "Market Centre" },
            { key: "unique_visitors", label: "Visitors", align: "right", sortValue: (row) => Number(row.unique_visitors ?? 0) },
            { key: "sessions", label: "Sessions", align: "right", sortValue: (row) => Number(row.sessions ?? 0) },
            { key: "page_views", label: "Page Views", align: "right", sortValue: (row) => Number(row.page_views ?? 0) },
            { key: "listing_views", label: "Listing Views", align: "right", sortValue: (row) => Number(row.listing_views ?? 0) },
            { key: "agent_site_views", label: "Agent Site Views", align: "right", sortValue: (row) => Number(row.agent_site_views ?? 0) },
            { key: "vcard_views", label: "VCard Views", align: "right", sortValue: (row) => Number(row.vcard_views ?? 0) },
            { key: "leads", label: "Leads", align: "right", sortValue: (row) => Number(row.leads ?? 0) },
            { key: "whatsapp_clicks", label: "WhatsApp", align: "right", sortValue: (row) => Number(row.whatsapp_clicks ?? 0) },
            { key: "finance_clicks", label: "Finance", align: "right", sortValue: (row) => Number(row.finance_clicks ?? 0) },
          ]}
        />
      </section>
    );
  };

  const renderPropertiesTab = () => {
    if (propertiesTab.loading) return <ReportState type="loading" message="Loading Properties analytics..." />;
    if (propertiesTab.error) return <ReportState type="error" message={propertiesTab.error} />;
    const rows = propertiesTab.data?.rows ?? [];
    const exportPropertiesCsv = () => {
      downloadCsv(`kwhomes-properties-${csvStamp()}.csv`, [[
        ["Listing Number", "Suburb / City", "Presenting Agent", "Market Centre", "Unique Visitors", "Listing Views", "WhatsApp", "Leads"],
        ...rows.map((row) => [row.listing_number, row.suburb, row.presenting_agent, row.market_centre, row.unique_visitors, row.views, row.whatsapp_clicks, row.leads]),
      ]]);
    };
    return (
      <section className="surface-card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Properties</h3>
          <ExportCsvButton onExport={exportPropertiesCsv} />
        </div>
        <AnalyticsTable<ListingTableRow>
          rows={rows}
          searchKeys={["listing_number", "suburb", "presenting_agent", "market_centre"]}
          initialSortKey="views"
          emptyMessage="No listing activity for this selection."
          columns={[
            { key: "listing_number", label: "Listing Number", render: (row) => (isValidKwuid(row.kwuid) ? <ExternalLink href={`${KWHOMES_BASE_URL}/${row.kwuid}/${row.listing_number}`}>{row.listing_number}</ExternalLink> : row.listing_number) },
            { key: "suburb", label: "Suburb / City" },
            { key: "presenting_agent", label: "Presenting Agent" },
            { key: "market_centre", label: "Market Centre" },
            { key: "unique_visitors", label: "Unique Visitors", align: "right", sortValue: (row) => Number(row.unique_visitors ?? 0) },
            { key: "views", label: "Listing Views", align: "right", sortValue: (row) => Number(row.views ?? 0) },
            { key: "whatsapp_clicks", label: "WhatsApp", align: "right", sortValue: (row) => Number(row.whatsapp_clicks ?? 0) },
            { key: "leads", label: "Leads", align: "right", sortValue: (row) => Number(row.leads ?? 0) },
          ]}
        />
      </section>
    );
  };

  const renderVCardsTab = () => {
    if (vcardsTab.loading) return <ReportState type="loading" message="Loading VCards analytics..." />;
    if (vcardsTab.error) return <ReportState type="error" message={vcardsTab.error} />;
    const rows = vcardsTab.data?.rows ?? [];
    const exportVcardsCsv = () => {
      downloadCsv(`kwhomes-vcards-${csvStamp()}.csv`, [[
        ["Agent", "Market Centre", "VCard Views", "Unique Visitors", "Save Contact", "Share / Copy Link", "WhatsApp", "Call", "Email"],
        ...rows.map((row) => [row.agent, row.market_centre, row.vcard_views, row.unique_visitors, row.save_contact_clicks, row.share_clicks, row.whatsapp_clicks, row.call_clicks, row.email_clicks]),
      ]]);
    };
    return (
      <section className="surface-card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">VCards</h3>
          <ExportCsvButton onExport={exportVcardsCsv} />
        </div>
        <AnalyticsTable<VcardTableRow>
          rows={rows}
          searchKeys={["agent", "market_centre"]}
          initialSortKey="vcard_views"
          emptyMessage="No VCard activity for this selection."
          columns={[
            { key: "agent", label: "Agent", render: (row) => (isValidKwuid(row.kwuid) ? <ExternalLink href={`${KWHOMES_BASE_URL}/vcard/${row.kwuid}`}>{row.agent}</ExternalLink> : row.agent) },
            { key: "market_centre", label: "Market Centre" },
            { key: "vcard_views", label: "VCard Views", align: "right", sortValue: (row) => Number(row.vcard_views ?? 0) },
            { key: "unique_visitors", label: "Unique Visitors", align: "right", sortValue: (row) => Number(row.unique_visitors ?? 0) },
            { key: "save_contact_clicks", label: "Save Contact", align: "right", sortValue: (row) => Number(row.save_contact_clicks ?? 0) },
            { key: "share_clicks", label: "Share / Copy Link", align: "right", sortValue: (row) => Number(row.share_clicks ?? 0) },
            { key: "whatsapp_clicks", label: "WhatsApp", align: "right", sortValue: (row) => Number(row.whatsapp_clicks ?? 0) },
            { key: "call_clicks", label: "Call", align: "right", sortValue: (row) => Number(row.call_clicks ?? 0) },
            { key: "email_clicks", label: "Email", align: "right", sortValue: (row) => Number(row.email_clicks ?? 0) },
          ]}
        />
      </section>
    );
  };

  const renderLeadsTab = () => {
    if (leadsTab.loading) return <ReportState type="loading" message="Loading Leads analytics..." />;
    if (leadsTab.error) return <ReportState type="error" message={leadsTab.error} />;
    const leads = leadsTab.data;
    if (!leads) return <ReportState type="empty" message="No lead data is available for this selection." />;
    const maxDay = Math.max(1, ...leads.by_day.map((point) => point.leads));
    const exportLeadsCsv = () => {
      downloadCsv(`kwhomes-leads-${csvStamp()}.csv`, [
        [["Summary"], ["Total Leads", leads.total], ["Source", leads.source]],
        [["Leads Over Time"], ["Date", "Leads"], ...leads.by_day.map((point) => [point.date, point.leads])],
        [["Leads by Agent"], ["Agent", "Market Centre", "Leads"], ...leads.by_agent.map((row) => [row.agent, row.market_centre, row.leads])],
        [["Leads by Market Centre"], ["Market Centre", "Leads"], ...leads.by_market_centre.map((row) => [row.market_centre, row.leads])],
        [
          ["Recent Leads (no consumer PII)"],
          ["Date / Time", "Agent", "Market Centre", "Listing", "Type", "Source / Origin"],
          ...leads.recent.map((row) => [new Date(row.created_at).toLocaleString(), row.agent, row.market_centre, row.listing_number ?? "", row.lead_type ?? "", row.lead_source ?? ""]),
        ],
      ]);
    };
    return (
      <>
        <div className="flex justify-end">
          <ExportCsvButton onExport={exportLeadsCsv} />
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <StatCard label="Total Leads" value={String(leads.total)} icon={<ReportIcon kind="check-circle" className="h-4 w-4" />} />
          <StatCard label="Agents with Leads" value={String(leads.by_agent.length)} icon={<ReportIcon kind="users" className="h-4 w-4" />} />
          <StatCard label="Market Centres with Leads" value={String(leads.by_market_centre.length)} icon={<ReportIcon kind="target" className="h-4 w-4" />} />
        </div>
        <section className="surface-card p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Leads Over Time</h3>
          <div className="flex h-40 items-end gap-1 overflow-visible">
            {leads.by_day.length === 0 ? (
              <p className="text-sm text-slate-500">No leads recorded for this range.</p>
            ) : (
              leads.by_day.map((point) => (
                <div key={point.date} className="group relative flex flex-1 flex-col items-center gap-1">
                  <div className="pointer-events-none absolute bottom-12 left-1/2 z-10 hidden w-36 -translate-x-1/2 rounded-md border border-slate-200 bg-white p-2 text-xs shadow-lg group-hover:block group-focus-within:block">
                    <p className="mb-1 font-semibold text-slate-700">
                      {new Date(`${point.date}T00:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                    <div className="flex items-center justify-between gap-2 py-0.5 text-slate-600">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden="true" />
                        Leads
                      </span>
                      <span className="font-semibold text-slate-800">{point.leads}</span>
                    </div>
                  </div>
                  <div className="flex h-32 w-full items-end justify-center">
                    <div
                      className="w-2/3 rounded-t bg-emerald-400"
                      style={{ height: `${Math.max(8, (point.leads / maxDay) * 100)}%` }}
                      aria-label={`${point.date}: ${point.leads} leads`}
                    />
                  </div>
                  <span className="text-[10px] text-slate-500">
                    {new Date(`${point.date}T00:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                </div>
              ))
            )}
          </div>
        </section>
        <div className="grid gap-4 xl:grid-cols-2">
          <section className="surface-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Leads by Agent</h3>
            <AnalyticsTable
              rows={leads.by_agent}
              searchKeys={["agent", "market_centre"]}
              initialSortKey="leads"
              emptyMessage="No leads attributed to agents in this range."
              columns={[
                { key: "agent", label: "Agent" },
                { key: "market_centre", label: "Market Centre" },
                { key: "leads", label: "Leads", align: "right", sortValue: (row) => Number(row.leads ?? 0) },
              ]}
            />
          </section>
          <section className="surface-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Leads by Market Centre</h3>
            <AnalyticsTable
              rows={leads.by_market_centre}
              searchKeys={["market_centre"]}
              initialSortKey="leads"
              emptyMessage="No leads attributed to a Market Centre in this range."
              columns={[
                { key: "market_centre", label: "Market Centre" },
                { key: "leads", label: "Leads", align: "right", sortValue: (row) => Number(row.leads ?? 0) },
              ]}
            />
          </section>
        </div>
        <section className="surface-card p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Recent Leads (aggregated, no consumer PII)</h3>
          <AnalyticsTable
            rows={leads.recent}
            searchKeys={["agent", "market_centre", "listing_number", "lead_type", "lead_source"]}
            initialSortKey="created_at"
            emptyMessage="No leads recorded for this range."
            columns={[
              { key: "created_at", label: "Date / Time", render: (row) => new Date(row.created_at).toLocaleString() },
              { key: "agent", label: "Agent" },
              { key: "market_centre", label: "Market Centre" },
              { key: "listing_number", label: "Listing", render: (row) => row.listing_number ?? "\u2013" },
              { key: "lead_type", label: "Type", render: (row) => row.lead_type ?? "\u2013" },
              { key: "lead_source", label: "Source / Origin", render: (row) => row.lead_source ?? "\u2013" },
            ]}
          />
        </section>
      </>
    );
  };

  const renderTrafficTab = () => {
    if (trafficTab.loading) return <ReportState type="loading" message="Loading Traffic analytics..." />;
    if (trafficTab.error) return <ReportState type="error" message={trafficTab.error} />;
    const traffic = trafficTab.data;
    if (!traffic) return <ReportState type="empty" message="No traffic data is available for this selection." />;
    const maxVisitors = Math.max(1, ...traffic.traffic_over_time.map((point) => point.visitors));
    const exportTrafficCsv = () => {
      downloadCsv(`kwhomes-traffic-${csvStamp()}.csv`, [
        [
          ["Human Summary"],
          ["Unique Visitors", traffic.human.unique_visitors],
          ["Sessions", traffic.human.sessions],
          ["Page Views", traffic.human.page_views],
          ["Pages / Session", traffic.human.pages_per_session],
        ],
        [["Traffic Over Time"], ["Date", "Visitors", "Sessions", "Page Views"], ...traffic.traffic_over_time.map((point) => [point.date, point.visitors, point.sessions, point.page_views])],
        [["Traffic Sources"], ["Source", "Sessions"], ...traffic.traffic_sources.map((row) => [row.source, row.sessions])],
        [["Device Split"], ["Device", "Sessions"], ...traffic.device_split.map((row) => [row.device, row.sessions])],
        [["Top Page Types"], ["Page Type", "Events"], ...traffic.top_page_types.map((row) => [row.page_type, row.events])],
        [["Top Entry Pages"], ["Page Path", "Sessions"], ...traffic.top_entry_pages.map((row) => [row.page_path, row.sessions])],
        [
          ["Automated / Crawler Traffic"],
          ["Crawler Sessions", traffic.bot_diagnostics.bot_sessions],
          ["Crawler Events", traffic.bot_diagnostics.bot_events],
          ["% of Raw Traffic", traffic.bot_diagnostics.bot_pct_of_raw],
          ["Last Observed", traffic.bot_diagnostics.last_observed ?? ""],
        ],
        [["Top Crawler Categories"], ["Category", "Sessions", "Events"], ...traffic.bot_diagnostics.top_categories.map((row) => [row.category, row.sessions, row.events])],
      ]);
    };
    return (
      <>
        <div className="flex justify-end">
          <ExportCsvButton onExport={exportTrafficCsv} />
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <StatCard label="Human Unique Visitors" value={traffic.human.unique_visitors.toLocaleString()} icon={<ReportIcon kind="users" className="h-4 w-4" />} />
          <StatCard label="Human Sessions" value={traffic.human.sessions.toLocaleString()} icon={<ReportIcon kind="chart" className="h-4 w-4" />} />
          <StatCard label="Human Page Views" value={traffic.human.page_views.toLocaleString()} icon={<ReportIcon kind="gauge" className="h-4 w-4" />} />
          <StatCard label="Pages / Session" value={traffic.human.pages_per_session.toFixed(2)} icon={<ReportIcon kind="layers" className="h-4 w-4" />} />
        </div>

        <section className="surface-card p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Traffic Over Time (human only)</h3>
          <div className="flex h-40 items-end gap-1 overflow-visible">
            {traffic.traffic_over_time.length === 0 ? (
              <p className="text-sm text-slate-500">No traffic data for this range.</p>
            ) : (
              traffic.traffic_over_time.map((point) => (
                <div key={point.date} className="group relative flex flex-1 flex-col items-center gap-1">
                  <div className="pointer-events-none absolute bottom-12 left-1/2 z-10 hidden w-36 -translate-x-1/2 rounded-md border border-slate-200 bg-white p-2 text-xs shadow-lg group-hover:block group-focus-within:block">
                    <p className="mb-1 font-semibold text-slate-700">
                      {new Date(`${point.date}T00:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                    <div className="flex items-center justify-between gap-2 py-0.5 text-slate-600">
                      <span>Visitors</span>
                      <span className="font-semibold text-slate-800">{point.visitors}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 py-0.5 text-slate-600">
                      <span>Sessions</span>
                      <span className="font-semibold text-slate-800">{point.sessions}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 py-0.5 text-slate-600">
                      <span>Page Views</span>
                      <span className="font-semibold text-slate-800">{point.page_views}</span>
                    </div>
                  </div>
                  <div className="flex h-32 w-full items-end justify-center">
                    <div className="w-2/3 rounded-t bg-red-400" style={{ height: `${Math.max(8, (point.visitors / maxVisitors) * 100)}%` }} aria-label={`${point.date}: ${point.visitors} visitors`} />
                  </div>
                  <span className="text-[10px] text-slate-500">
                    {new Date(`${point.date}T00:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                </div>
              ))
            )}
          </div>
        </section>

        <div className="grid gap-4 xl:grid-cols-3">
          <section className="surface-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Traffic Sources (session-based)</h3>
            <div className="space-y-3">
              {traffic.traffic_sources.map((item) => (
                <div key={item.source}>
                  <div className="mb-1 flex justify-between text-xs text-slate-600">
                    <span>{item.source}</span>
                    <span>{item.sessions}</span>
                  </div>
                  <div className="h-2 rounded bg-slate-200">
                    <div className="h-2 rounded bg-red-500" style={{ width: `${Math.max(8, (item.sessions / Math.max(1, ...traffic.traffic_sources.map((row) => row.sessions))) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section className="surface-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Device Split (session-based)</h3>
            <div className="space-y-3">
              {traffic.device_split.map((item) => (
                <div key={item.device}>
                  <div className="mb-1 flex justify-between text-xs text-slate-600">
                    <span>{item.device}</span>
                    <span>{item.sessions}</span>
                  </div>
                  <div className="h-2 rounded bg-slate-200">
                    <div className="h-2 rounded bg-sky-500" style={{ width: `${Math.max(8, (item.sessions / Math.max(1, ...traffic.device_split.map((row) => row.sessions))) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section className="surface-card p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Top Page Types</h3>
            <div className="space-y-2">
              {traffic.top_page_types.map((item) => (
                <div key={item.page_type} className="flex justify-between text-xs text-slate-600">
                  <span className="capitalize">{item.page_type.replace(/_/g, " ")}</span>
                  <span className="font-semibold text-slate-800">{item.events}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <section className="surface-card p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">Top Entry Pages</h3>
          <AnalyticsTable
            rows={traffic.top_entry_pages}
            searchKeys={["page_path"]}
            initialSortKey="sessions"
            emptyMessage="No entry-page data for this selection."
            columns={[
              { key: "page_path", label: "Page Path", render: (row) => (isValidKwhomesPath(row.page_path) ? <ExternalLink href={`${KWHOMES_BASE_URL}${row.page_path}`}>{row.page_path}</ExternalLink> : row.page_path) },
              { key: "sessions", label: "Sessions", align: "right", sortValue: (row) => Number(row.sessions ?? 0) },
            ]}
          />
        </section>

        <section className="surface-card border-t-4 border-amber-400 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Automated / Crawler Traffic (diagnostic only)</h3>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">Excluded from human KPIs above</span>
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            <StatCard label="Crawler Sessions" value={traffic.bot_diagnostics.bot_sessions.toLocaleString()} icon={<ReportIcon kind="target" className="h-4 w-4" />} />
            <StatCard label="Crawler Events" value={traffic.bot_diagnostics.bot_events.toLocaleString()} icon={<ReportIcon kind="gauge" className="h-4 w-4" />} />
            <StatCard label="% of Raw Traffic" value={`${traffic.bot_diagnostics.bot_pct_of_raw}%`} icon={<ReportIcon kind="chart" className="h-4 w-4" />} />
            <StatCard
              label="Last Observed"
              value={traffic.bot_diagnostics.last_observed ? new Date(traffic.bot_diagnostics.last_observed).toLocaleString() : "\u2013"}
              icon={<ReportIcon kind="calendar" className="h-4 w-4" />}
            />
          </div>
          <div className="mt-4">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Top Crawler Categories</h4>
            <AnalyticsTable
              rows={traffic.bot_diagnostics.top_categories}
              searchKeys={["category"]}
              initialSortKey="events"
              emptyMessage="No crawler traffic detected for this selection."
              columns={[
                { key: "category", label: "Category" },
                { key: "sessions", label: "Sessions", align: "right", sortValue: (row) => Number(row.sessions ?? 0) },
                { key: "events", label: "Events", align: "right", sortValue: (row) => Number(row.events ?? 0) },
              ]}
            />
          </div>
        </section>
      </>
    );
  };

  if (!canViewAnalytics) {
    return <ReportState type="error" message="KWHomes Analytics is unavailable for this account." />;
  }

  return (
    <div className="space-y-4">
      <ReportHeader
        title="KWHomes Analytics"
        subtitle="KWHomes ecosystem activity, scoped to your account."
        context={
          data
            ? `Lead KPI: ${data.lead_count_source} | Traffic chart leads: lead_submit events`
            : "Local dev, no production data"
        }
      />

      <section className="surface-card p-4">
        <div className="mb-4 flex flex-wrap gap-2">
          {tabOptions.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={classNames(
                "rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
                activeTab === tab
                  ? "border-red-500 bg-red-50 text-red-700"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
              )}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Date Range
            </span>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              {dateRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {dateRange === "custom" && (
            <>
              <label className="text-sm">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  From
                </span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  To
                </span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </label>
            </>
          )}

          {canSelectMarketCentre && <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Market Centre
            </span>
            <select
              value={filterMarketCentreId}
              disabled={isOfficeAdmin && !isRegionalAdminAccount}
              onChange={(e) => setMarketCentreId(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              {isOfficeAdmin && !isRegionalAdminAccount ? (
                <option value={filterMarketCentreId}>
                  {activeContext?.marketCenter ?? data?.filters.market_centres.find((option) => option.id === filterMarketCentreId)?.name ?? "Assigned Market Centre"}
                </option>
              ) : (
                <>
                  <option value="">All Market Centres</option>
                  {(data?.filters.market_centres ?? []).map((option, index) => (
                    <option key={`${option.id}-${option.name}-${index}`} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </label>}

          {canSelectAgent && <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Agent
            </span>
            <AgentFilterCombobox
              key={`${activeContextId}-${agentFilterResetKey}`}
              token={localStorage.getItem("kwsa_auth_token")}
              activeContextId={activeContextId}
              marketCentreId={filterMarketCentreId}
              value={effectiveAgentKwuid}
              valueLabel={effectiveAgentName}
              onSelect={(kwuid, name) => {
                setAgentKwuid(kwuid);
                setAgentName(name);
                setAgentContextId(activeContextId);
              }}
            />
          </label>}

          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Page Type
            </span>
            <select
              value={pageType}
              onChange={(e) => setPageType(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">All</option>
              <option value="agent_site">Agent Site</option>
              <option value="region">Region</option>
              <option value="market_centre">Market Centre</option>
              <option value="listing">Listing</option>
              <option value="vcard">VCard</option>
              <option value="search">Search</option>
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Traffic Source
            </span>
            <select
              value={trafficSource}
              onChange={(e) => setTrafficSource(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">All Sources</option>
              <option value="Direct">Direct</option>
              <option value="Google">Google</option>
              <option value="WhatsApp">WhatsApp</option>
              <option value="Facebook">Facebook</option>
              <option value="Email">Email</option>
              <option value="Other">Other</option>
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Device
            </span>
            <select
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">All Devices</option>
              <option value="Mobile">Mobile</option>
              <option value="Desktop">Desktop</option>
              <option value="Tablet">Tablet</option>
            </select>
          </label>
        </div>
        <div className="mt-3 flex flex-wrap justify-end">
          <button
            type="button"
            onClick={clearFilters}
            disabled={isFiltersDefault}
            className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
              <path
                d="M4 4v4h4M16 16v-4h-4M4.5 8.5A6 6 0 0115.2 6M15.5 11.5A6 6 0 014.8 14"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Clear Filters
          </button>
        </div>
      </section>

      {activeTab === "Email Reports" ? <EmailReportsTab /> : null}
      {activeTab === "Overview" ? renderOverview() : null}
      {activeTab === "Agents" ? renderAgentsTab() : null}
      {activeTab === "Market Centres" ? renderMarketCentresTab() : null}
      {activeTab === "Properties" ? renderPropertiesTab() : null}
      {activeTab === "VCards" ? renderVCardsTab() : null}
      {activeTab === "Leads" ? renderLeadsTab() : null}
      {activeTab === "Traffic" ? renderTrafficTab() : null}
    </div>
  );
}
